import {
  User,
  AlertCircle,
  Lock,
  Settings,
  Bell,
  Smile,
  ShieldAlert,
} from "lucide-react";
import { FaFileWord } from "react-icons/fa";
import { useAuth } from "@/features/auth";
import { useBootstrap } from "@/features/bootstrap";
import { useAlerts } from "@/hooks/useAlerts";
import { useWordBank } from "@/hooks/useWordBank";
import { useSettingsState } from "./hooks/useSettingsState";
import { useOrgScope } from "./hooks/useOrgScope";
import { useScrollNav } from "./hooks/useScrollNav";
import { NavItem } from "./components/NavItem";
import { MobileAccordion } from "./components/MobileAccordion";
import { AccountSection } from "./sections/AccountSection";
import { NotificationsSection } from "./sections/NotificationsSection";
import { PrivacySection } from "./sections/PrivacySection";
import { SystemSection } from "./sections/SystemSection";
import EmotionAlertsGrid from "./EmotionAlertsGrid";
import WordAlertSettings from "./WordAlertSettings";
import AppAccessControl from "@/features/access-control";
import { ROLES } from "@/features/router/roles";
import { useAppAccess } from "@/features/access-control";

const hasAccessControl = (role) => {
  return role === ROLES.SUPERADMIN || role === ROLES.ADMIN;
};

const OptionsScreen = (props) => {
  const { logout } = useAuth();
  const { data: user } = useBootstrap();
  const settings = useSettingsState();
  const orgScope = useOrgScope();
  const nav = useScrollNav();
  const { alerts } = useAlerts();
  const {
    wordBankQuery,
    createWordsMutation,
    updateWordMutation,
    deleteWordMutation,
  } = useWordBank();
  const { data: wordBank, isLoading: wordBankIsLoading } = wordBankQuery;
  const { isPaused } = useAppAccess();

  const isStaff = user.role !== ROLES.STUDENT;
  const canManageAccess = hasAccessControl(user.role);

  const settingsProps = {
    handleSettingChange: settings.handleSettingChange,
    dataUse: settings.dataUse,
    setDataUse: settings.setDataUse,
    receiveEmailAlerts: settings.receiveEmailAlerts,
    setReceiveEmailAlerts: settings.setReceiveEmailAlerts,
    receiveReports: settings.receiveReports,
    setReceiveReports: settings.setReceiveReports,
    wellnessTimer: settings.wellnessTimer,
    setWellnessTimer: settings.setWellnessTimer,
    adultActionImages: settings.adultActionImages,
    setAdultActionImages: settings.setAdultActionImages,
    usesCastle: settings.usesCastle,
    setUsesCastle: settings.setUsesCastle,
    voice: settings.voice,
    setVoice: settings.setVoice,
    showTourButton: isStaff,
    onboardingUserKey: user?._id || user?.email || "unknown",
  };

  const alertProps = {
    props,
    userScope: orgScope.userScope,
    schoolId: orgScope.schoolId,
    classIds: orgScope.classIds,
    districtId: orgScope.districtId,
  };

  return (
    <div className="tw">
      <div className="sm:ml-[130px] flex flex-col items-center bg-gray-50 min-h-screen">
        <div
          ref={nav.topBarRef}
          className="flex flex-row justify-between w-full gap-4 px-4 pb-3 pt-16 sm:px-8 sm:py-3 sticky z-10 top-0 border-b border-gray-200 bg-gray-50/80 backdrop-blur-sm"
        >
          <span className="font-bold text-[24px]">Settings</span>
          <button
            className="bg-[#256AA6] hover:bg-[#1e5a8f] py-1.5 px-5 rounded-lg text-white font-semibold text-sm transition-colors duration-200 shadow-sm"
            onClick={logout}
          >
            Log Out
          </button>
        </div>
        {nav.onMobile ? (
          <div
            className="flex flex-col w-full gap-3 px-3 sm:px-8 sm:py-4 py-3"
            data-onboarding-id="settings-nav-tabs"
          >
            <div className="flex flex-col w-full space-y-3">
              <MobileAccordion
                id="account"
                open={nav.mobileOpen}
                setOpen={nav.setMobileOpen}
                icon={<User size={14} />}
                label="Account & Profile"
              >
                <div className="p-3">
                  <AccountSection />
                </div>
              </MobileAccordion>
              {isStaff && (
                <MobileAccordion
                  id="emotion-alerts"
                  open={nav.mobileOpen}
                  setOpen={nav.setMobileOpen}
                  icon={<Smile size={14} />}
                  label="Emotion Alerts"
                >
                  <div className="bg-white flex flex-col w-full">
                    <EmotionAlertsGrid
                      alerts={alerts}
                      isTeacher={user.role === ROLES.TEACHER}
                      {...alertProps}
                    />
                  </div>
                </MobileAccordion>
              )}
              {isStaff && (
                <MobileAccordion
                  id="word-alerts"
                  open={nav.mobileOpen}
                  setOpen={nav.setMobileOpen}
                  icon={<FaFileWord size={14} />}
                  label="Word Alerts"
                >
                  <div className="bg-white flex flex-col w-full">
                    <WordAlertSettings
                      data={wordBank}
                      isLoading={wordBankIsLoading}
                      updateWordMutation={updateWordMutation}
                      deleteWordMutation={deleteWordMutation}
                      createWordsMutation={createWordsMutation}
                      user={user}
                      {...alertProps}
                    />
                  </div>
                </MobileAccordion>
              )}
              {isStaff && (
                <MobileAccordion
                  id="notifications"
                  open={nav.mobileOpen}
                  setOpen={nav.setMobileOpen}
                  icon={<Bell size={14} />}
                  label="Notification"
                >
                  <NotificationsSection {...settingsProps} />
                </MobileAccordion>
              )}
              <MobileAccordion
                id="privacy"
                open={nav.mobileOpen}
                setOpen={nav.setMobileOpen}
                icon={<Lock size={14} />}
                label="Privacy & Data"
              >
                <PrivacySection {...settingsProps} />
              </MobileAccordion>
              {(!isPaused || isStaff) && (
                <MobileAccordion
                  id="system"
                  open={nav.mobileOpen}
                  setOpen={nav.setMobileOpen}
                  icon={<Settings size={14} />}
                  label="System & Display"
                >
                  <SystemSection mobile {...settingsProps} />
                </MobileAccordion>
              )}
              {canManageAccess && (
                <MobileAccordion
                  id="access-control"
                  open={nav.mobileOpen}
                  setOpen={nav.setMobileOpen}
                  icon={<ShieldAlert size={14} />}
                  label="App Access Control"
                >
                  <div className="p-3">
                    <AppAccessControl user={user} />
                  </div>
                </MobileAccordion>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-row w-full gap-3 px-3 sm:px-8 sm:py-4 py-3">
            <div
              className="h-fit w-[20%] bg-white rounded sticky left-12"
              data-onboarding-id="settings-nav-tabs"
              style={{ top: nav.navTopOffset }}
            >
              <div className="flex flex-col p-3">
                <NavItem
                  section="account"
                  icon={<User size={14} />}
                  label="Account & Profile"
                  selected={nav.selected}
                  onClick={nav.handleScroll}
                />
                {isStaff && (
                  <NavItem
                    section="alerts"
                    icon={<AlertCircle size={14} />}
                    label="Alerts"
                    selected={nav.selected}
                    onClick={nav.handleScroll}
                  />
                )}
                {isStaff && (
                  <NavItem
                    section="notifications"
                    icon={<Bell size={14} />}
                    label="Notification"
                    selected={nav.selected}
                    onClick={nav.handleScroll}
                  />
                )}
                <NavItem
                  section="privacy"
                  icon={<Lock size={14} />}
                  label="Privacy & Data"
                  selected={nav.selected}
                  onClick={nav.handleScroll}
                />
                {(!isPaused || isStaff) && (
                  <NavItem
                    section="system"
                    icon={<Settings size={14} />}
                    label="System & Display"
                    selected={nav.selected}
                    onClick={nav.handleScroll}
                  />
                )}
                {canManageAccess && (
                  <NavItem
                    section="access-control"
                    icon={<ShieldAlert size={14} />}
                    label="App Access"
                    selected={nav.selected}
                    onClick={nav.handleScroll}
                  />
                )}
              </div>
            </div>
            <div className="w-[80%] flex flex-col gap-2.5">
              <div
                ref={nav.accountRef}
                className="flex flex-col bg-white rounded p-4"
              >
                <div className="flex flex-row gap-2 pb-2.5 items-center">
                  <User size={14} className="shrink-0" />
                  Account & Profile
                </div>
                <div className="p-2.5 border border-[#DDDDDD] rounded">
                  <AccountSection />
                </div>
              </div>
              {isStaff && (
                <div
                  ref={nav.alertsRef}
                  className="bg-white rounded flex flex-col p-2.5 w-full"
                >
                  <EmotionAlertsGrid
                    alerts={alerts}
                    isTeacher={user?.role === ROLES.TEACHER}
                    {...alertProps}
                  />
                </div>
              )}
              {isStaff && (
                <div className="bg-white rounded flex flex-col p-2.5 w-full">
                  <WordAlertSettings
                    data={wordBank}
                    isLoading={wordBankIsLoading}
                    updateWordMutation={updateWordMutation}
                    deleteWordMutation={deleteWordMutation}
                    createWordsMutation={createWordsMutation}
                    user={user}
                    {...alertProps}
                  />
                </div>
              )}
              {isStaff && (
                <div
                  ref={nav.notificationsRef}
                  className="bg-white rounded flex flex-col p-4 w-full"
                >
                  <div className="flex flex-row gap-2 pb-2.5 items-center">
                    <Bell size={14} className="shrink-0" />
                    NOTIFICATIONS
                  </div>
                  <NotificationsSection {...settingsProps} />
                </div>
              )}

              <div
                ref={nav.privacyRef}
                className="bg-white rounded flex flex-col p-4 w-full"
              >
                <div className="flex flex-row gap-2 pb-2.5 items-center">
                  <Lock size={14} className="shrink-0" />
                  PRIVACY & DATA
                </div>
                <PrivacySection {...settingsProps} />
              </div>
              {(!isPaused || isStaff) && (
                <div
                  ref={nav.systemRef}
                  className="bg-white rounded flex flex-col p-4 gap-2.5 w-full"
                >
                  <div className="flex flex-row gap-2 items-center">
                    <Settings size={14} className="shrink-0" />
                    SYSTEM & DISPLAY
                  </div>
                  <SystemSection {...settingsProps} />
                </div>
              )}
              {canManageAccess && user && (
                <div
                  ref={nav.accessControlRef}
                  className="bg-white rounded flex flex-col p-4 gap-2.5 w-full"
                >
                  <div className="flex flex-row gap-2 items-center">
                    <ShieldAlert size={14} className="shrink-0" />
                    App ON/OFF Controls
                  </div>
                  <div className="border border-[#DDDDDD] rounded p-3">
                    <AppAccessControl user={user} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OptionsScreen;
